/*eslint react-hooks/exhaustive-deps: "off"*/
/*eslint no-unused-vars: "off"*/
import React, { useEffect, useState } from "react";
import MapBase from "./MapBase";
import { PALETTE_YlOrRd_9, TRANSPARENT_COLOR } from "../../utils/Palette";
import useMapProps from "./useMapProps";
import { MapPropsProvider } from "./MapPropsContext";
import WidgetContainer from "./widgets/WidgetContainer";
import { arrayToRgb, getApiUrl, getLastYear, makeTableSorterFn, numberWithCommas } from "../../utils";
import WidgetCodeForStats from "./widgets/WidgetCodeForStats";
import RMateChartFusionCountry from "./r_mate_chart/RMateChartFusionCountry";
import Seperator from "./widgets/Seperator";
import WidgetStatisticsTable from "./widgets/WidgetStatisticsTable";
import WidgetLegend from "./widgets/WidgetLegend";
import WidgetSelectYear from "./widgets/WidgetSelectYear";
import WidgetRisingTabs from "./widgets/WidgetRisingTabs";
import WidgetDistrictSelect from "./widgets/WidgetDistrictSelect";
import WidgetToolbar from "./widgets/WidgetToolbar";
import { WELL_KNOWN_POSITION } from "../../Constants";
import AlignRightText from "./widgets/AlignRightText";

const CLS_CODE_DICT = {
  32: "기지국",
  94: "간이무선국",
  44: "육상무선국",
  85: "이동중계국",
  49: "이동국",
  92: "아마추어국",
  42: "의무선박국",
  41: "선박국",
  21: "고정국",
  CMFL: "전파응용설비",
  OTHER: "기타",
};

function MapFusionCountry() {
  const [fillColorLegend, setFillColorLegend] = useState([]);
  const [searchYear, setSearchYear] = useState("");

  const mapProps = useMapProps({
    INITIAL_VIEW_STATE: {
      ...WELL_KNOWN_POSITION.세종특별자치시,
      zoom: 8,
    },
    INITIAL_GEOJSON_EXTRA_PARAMS: {
      year: getLastYear(),
      month: 12,
      currentDistrictCode: "",
    },
    INITIAL_GEOJSON_POLYGON_LAYER_PROPS: {
      getHoverHtml: (object, mapDistrictStats) => {
        const [metricObject] = mapDistrictStats;
        const {
          properties: { pk, name },
        } = object;
        const code = pk;
        const obj = metricObject[code];
        if (obj) {
          const { size, total, total2, density } = obj;
          return `<span class='fontStyle'>${name}</span><br/>
                  용도면적합계 : ${numberWithCommas(size.toFixed(2))} ㎢<br/>
                  무선국 밀도 : ${numberWithCommas(density.toFixed(2))} (국수/㎢)`;
        }
        return `<span class='fontStyle'>${name}</span><br/>
                용도면적합계 : 0 ㎢<br/>
                무선국 밀도 : 0 (국수/㎢)`;
      },
      getLevelFillColor: ({ properties: { pk } }, mapDistrictStats) => {
        const [metricObject, metricMax] = mapDistrictStats;
        const code = pk;
        const obj = metricObject[code];
        // 데이터가 없더라도 색을 입힙니다.
        if (obj) {
          const { total } = metricObject[code] || { total: 1 };
          if (total >= 1) {
            const index = Math.floor((PALETTE_YlOrRd_9.length * (total - 1)) / metricMax);
            return PALETTE_YlOrRd_9[index];
          }
          return TRANSPARENT_COLOR;
        }
        return TRANSPARENT_COLOR;
      },
    },
    INITIAL_WIDGET_STATE: {
      service_code_stats_list: ["2G", "3G", "LTE", "5G"],
      cls_code_stats_list: Object.keys(CLS_CODE_DICT),
      //isClsSearch: false, // 서비스를 기본 조건으로 설정함
      isClsSearch: true, // 무선국종을 기본 조건으로 설정함
    },
  });

  // chkRadSvcString 인자로 가공됩니다.
  const [statsJsonSearchCodeList, setStatsJsonSearchCodeList] = useState(["2G", "3G", "LTE", "5G"]);

  // --- 서버 통신을 위한 함수 끝

  const {
    widgetState,
    dataResponseList,
    clickedPolygonGeojsonObjects,
    geojsonExtraParams,
    mapDistrictStats,
    setMapDistrictStats,
    isChangedMapDistrictStats,
    setGeojsonExtraParams,
    currentDistrictCode,
    setCurrentDistrictCode,
    setChartAjaxParams,
    setTableAjaxParams,
  } = mapProps;

  useEffect(() => {
    const { cls_code_stats_list, service_code_stats_list, isClsSearch } = widgetState;

    const { year } = geojsonExtraParams;
    setSearchYear(year);

    if (isClsSearch) {
      // 무선국종 검색조건일 경우
      setChartAjaxParams((prevParams) => ({
        ...prevParams,
        searchYear: year,
        prm_check2: cls_code_stats_list,
        gubun_code: "cls", // 코드에서만 사용. service 같음비교를 수행.
      }));
      setStatsJsonSearchCodeList(cls_code_stats_list);
    } else {
      // 서비스 검색조건일 경우
      setChartAjaxParams((prevParams) => ({
        ...prevParams,
        searchYear: year,
        prm_check: service_code_stats_list,
        gubun_code: "service", // 코드에서만 사용. service 같음비교를 수행.
      }));
      setStatsJsonSearchCodeList(service_code_stats_list);
    }
  }, [widgetState, geojsonExtraParams]);

  useEffect(() => {
    const metricObject = {};

    // FIXME: 현행 사이트 및 데이터에서 제주도 무선국이 도출되지 않습니다. 확인이 필요합니다.
    if (dataResponseList.length > 0 && statsJsonSearchCodeList.length > 0) {
      const firstDataLayer = dataResponseList[0];
      for (let [code, stats] of Object.entries(firstDataLayer)) {
        /*
        시군구 코드가 2일 때 전국지도가 띄워지게 된다.-> decorator.py에서 none 주석 해제하여 노출됨
         */
        if (code.length === 2) {
          // 전체의 용도면적 합계를 구합니다.
          let useageLandTotal = 0;
          let useageLandTotal2 = 0;
          for (let division_code of statsJsonSearchCodeList) {
            const divisionValue = stats[division_code];
            if (divisionValue) {
              // 해당하는 code의 값이 있을 경우, 무선국합계를 구한다.
              useageLandTotal += divisionValue.count;
              useageLandTotal2 += divisionValue.count2;
            }
          }
          const size = stats["ALL"]["area"];
          const total = useageLandTotal;
          const total2 = useageLandTotal2;

          if (total !== 0) {
            metricObject[code] = { total, total2, size, density: total / size };
          } else {
            metricObject[code] = { total, total2, size, density: 0 };
          }
        } else {
          // 시, 군구 단위를 볼 경우 용도별로 나뉘어져 한 depth 더 깊다.
          const lv2_statsOfAll = stats["전체"];
          const districtSize = lv2_statsOfAll["ALL"].area;
          const lv2_statsOfRest = Object.entries(stats)
            .filter(([key, value]) => key !== "전체")
            .map(([key, value]) => value);
          // 용도지역별 면적 합계를 담을 변수
          let useageLandSize = 0;
          // 해당하는 무선국에 들어가 있는 용도지역 면적을 담음
          let useageDepthLandSize = 0;
          // 용도지역별 무선국 수를 담을 변수
          let useageLandTotal = 0;
          let useageLandTotal2 = 0;

          for (let lv2_statsOfValue of lv2_statsOfRest) {
            for (let division_code of statsJsonSearchCodeList) {
              const divisionValue = lv2_statsOfValue[division_code];
              if (divisionValue) {
                // 해당하는 code의 값이 있을 경우
                // 용도지역 합계
                useageDepthLandSize = divisionValue.area;
                useageLandTotal += divisionValue.count;
                useageLandTotal2 += divisionValue.count2;
              }
            }
            useageLandSize += useageDepthLandSize;
          }
          // 용도지역 합계
          // const size = sum(lv2_statsOfRest.map((row) => {
          //   return (sum(Object.values(row).map(({area}) => area)));
          // }));
          const total = useageLandTotal;
          const total2 = useageLandTotal2;
          const size = useageLandSize;

          if (total !== 0) {
            metricObject[code] = { total, total2, size, density: total / districtSize };
          } else {
            metricObject[code] = { total, total2, size, density: 0 };
          }
        }
      }
    }
    const metricMax = Math.max(...Object.values(metricObject).map((obj) => obj.total));

    const newMapDistrictStats = [metricObject, metricMax];
    if (isChangedMapDistrictStats(newMapDistrictStats)) {
      setMapDistrictStats(newMapDistrictStats);
    }

    setChartAjaxParams((prevParams) => ({
      ...prevParams,
      sidoCode: currentDistrictCode || "",
      chkRadSvcString: JSON.stringify({
        data: statsJsonSearchCodeList.map((value) => ({ CHK_VALUE: value })),
      }),
    }));
  }, [dataResponseList, statsJsonSearchCodeList, currentDistrictCode]);

  /* ////1005_시군구 클릭 임시막기
  useEffect(() => {
    if (clickedPolygonGeojsonObjects.length > 0) {
      const firstLayer = clickedPolygonGeojsonObjects[0];
      const {properties: {name, pk}} = firstLayer;
      if ((pk || '').length <= 2) {
        setCurrentDistrictCode(pk);
      }
    }
  }, [clickedPolygonGeojsonObjects]);
  */

  useEffect(() => {
    const [metricObject, metricMax] = mapDistrictStats;
    if (window.isFinite(metricMax) && metricMax > 0) {
      setFillColorLegend(
        [...Array(9).keys()]
          .map((colorIndex) => {
            const boundary = (metricMax / 9) * (colorIndex + 1);
            const index = Math.floor((PALETTE_YlOrRd_9.length * (boundary - 1)) / metricMax);
            const colorRgb = arrayToRgb(PALETTE_YlOrRd_9[index]);
            return [colorRgb, boundary];
          })
          .reverse()
      );
    } else {
      // 데이터가 없을때
      setFillColorLegend(
        [...Array(1).keys()]
          .map((colorIndex) => {
            const boundary = 0;
            const colorRgb = "#ffffd9";
            return [colorRgb, boundary];
          })
          .reverse()
      );
    }
  }, [mapDistrictStats]);

  useEffect(() => {
    setGeojsonExtraParams((prev) => ({
      ...prev,
      currentDistrictCode,
    }));
  }, [currentDistrictCode]);

  useEffect(() => {
    // const { year } = geojsonExtraParams; // 이렇게 하면 요청이 4번씩 날라가서 로딩이 매우 길어집니다.

    setTableAjaxParams({
      sidoCode: currentDistrictCode || "",
      searchEnd: searchYear,
    });
  }, [currentDistrictCode, searchYear]);

  const { isClsSearch } = widgetState;

  return (
    <MapPropsProvider value={mapProps}>
      <MapBase isHideWidgetToggleClustering={true} isHideWidgetMapLayerTypes={true}>
        <WidgetContainer>
          <div>
            <WidgetDistrictSelect isShowDistrictLayerFromGeoserver={true} onlyLv1={true} />
            <WidgetSelectYear />
            <Seperator />
            <WidgetCodeForStats />
          </div>
        </WidgetContainer>
        <RMateChartFusionCountry
          disabled={
            (!isClsSearch && widgetState.service_code_stats_list.length === 0) ||
            (isClsSearch && widgetState.cls_code_stats_list.length === 0)
          }
        />
        {fillColorLegend.length > 0 && (
          <WidgetLegend fillColorLegend={fillColorLegend} titleColorLegend={"무선국 수"} unitColorLegend={"개"} />
        )}
      </MapBase>
      <WidgetToolbar />
    </MapPropsProvider>
  );
}

export default MapFusionCountry;
